// youtube/slate.tsx — THE FORMAT SLATE, as presentations.
//
// Generated by agents/remy/slate-video/scripts/gen-board-section.mjs. Do not
// hand-edit: slide counts and runtimes come from the files that were actually
// rendered and uploaded, so the board cannot claim a video it does not have.
//
// Every entry is a genuinely different DIRECTION — format, hook, pacing,
// narration voice and visual treatment — so this section is a set of real
// alternatives to choose between, not one idea graded eight times.
//
// Board law: a board is DocumentFrame | PresentationFrame | code diagram.
// This is the second kind. The section is ADDITIVE — nothing was removed.
//
// CACHE SALT 2026-08-13a — bumping this string changes the emitted file, which
// changes the main bundle hash, which mints a fresh /assets/index-*.js URL.
// Needed because Cloudflare's edge cached the SPA fallback HTML against the
// CORS variant of the previous hash: the bundle returned application/javascript
// on a plain GET but text/html when the request carried an Origin header — and
// the browser always sends Origin for a crossorigin module script, so the app
// hung on "Loading…" forever. A fresh hash is a clean cache key.

import { SectionBox } from "../SectionBox";
import { PresentationFrame } from "../PresentationFrame";

const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/youtube/slate";

type Entry = {
  slug: string; title: string; channel: string; treatment: string;
  hook: string; pacing: string; voice: string; portrait: boolean;
  slideCount: number; duration: string;
};

const SLATE: Entry[] = [
  {
    "slug": "01-stacked-kinetic",
    "title": "The AI Stack That Replaced A Full Time Hire",
    "channel": "Stacked",
    "treatment": "kinetic",
    "hook": "Cold stat — number before context",
    "pacing": "Fast · 3–5s beats · hard cuts",
    "voice": "am_michael",
    "portrait": false,
    "slideCount": 10,
    "duration": "0:45"
  },
  {
    "slug": "02-stacked-listicle",
    "title": "Five AI Tools Doing Real Work In Real Businesses",
    "channel": "Stacked",
    "treatment": "listicle",
    "hook": "Promise — a numbered payoff up front",
    "pacing": "Medium · 5–7s beats · rail-driven",
    "voice": "am_eric",
    "portrait": false,
    "slideCount": 8,
    "duration": "0:39"
  },
  {
    "slug": "03-worldmap-calm",
    "title": "The Four Places Every Ship On Earth Passes Through",
    "channel": "World Map",
    "treatment": "map",
    "hook": "Question — curiosity gap, no number",
    "pacing": "Slow · 6–8s beats · breathing room",
    "voice": "am_adam",
    "portrait": false,
    "slideCount": 9,
    "duration": "0:45"
  },
  {
    "slug": "04-worldmap-urgent",
    "title": "The Quiet Fight Over The Cables Under The Sea",
    "channel": "World Map",
    "treatment": "newsbrief",
    "hook": "Hard news — stakes in the first sentence",
    "pacing": "Urgent · 4–5s beats · ticker under everything",
    "voice": "am_onyx",
    "portrait": false,
    "slideCount": 9,
    "duration": "0:48"
  },
  {
    "slug": "05-ai-discoveries",
    "title": "The Machines Are Reading The Papers Nobody Read",
    "channel": "AI Discoveries",
    "treatment": "data",
    "hook": "Counterintuitive claim",
    "pacing": "Medium · count-ups on the figures",
    "voice": "bm_fable",
    "portrait": false,
    "slideCount": 9,
    "duration": "0:45"
  },
  {
    "slug": "06-human-nature",
    "title": "You Decided About Them Before They Spoke",
    "channel": "Human Nature",
    "treatment": "essay",
    "hook": "Second person — put the viewer in the scene",
    "pacing": "Slow · warm · typewriter reveals",
    "voice": "af_heart",
    "portrait": false,
    "slideCount": 9,
    "duration": "0:41"
  },
  {
    "slug": "07-short-vertical",
    "title": "You Picked A Side Before You Finished Reading",
    "channel": "Shorts (any channel)",
    "treatment": "short",
    "hook": "Pattern interrupt — accusation in frame one",
    "pacing": "Very fast · 2–3s beats · captions burned in",
    "voice": "am_puck",
    "portrait": true,
    "slideCount": 8,
    "duration": "0:26"
  },
  {
    "slug": "08-doc-container",
    "title": "The Box That Rebuilt The World Economy",
    "channel": "Documentary",
    "treatment": "doc",
    "hook": "Cold open — a scene, then the title",
    "pacing": "Slow · chaptered · letterboxed",
    "voice": "am_onyx",
    "portrait": false,
    "slideCount": 10,
    "duration": "0:49"
  },
  {
    "slug": "09-versus-hire-build",
    "title": "Hiring A Person Versus Building The System",
    "channel": "Stacked",
    "treatment": "versus",
    "hook": "Framed choice — two options on screen from frame one",
    "pacing": "Medium · every beat is a comparison",
    "voice": "bf_emma",
    "portrait": false,
    "slideCount": 9,
    "duration": "0:39"
  },
  {
    "slug": "10-diagram-request",
    "title": "What Actually Happens When You Ask A Model A Question",
    "channel": "AI Discoveries",
    "treatment": "diagram",
    "hook": "Mechanism — promise to show the inside of something",
    "pacing": "Steady · one node per beat, the picture builds",
    "voice": "am_liam",
    "portrait": false,
    "slideCount": 9,
    "duration": "0:43"
  }
];

/** The films alone, no container. The board composes these INTO its own
 *  gallery SectionBox alongside the flagship episodes, so the slate is one
 *  shelf of a single gallery rather than its own eleventh section. */
export function SlateFilmGrid({ cols = 4 }: { cols?: number }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, max-content)`, gap: 40 }}>
      {SLATE.map((v) => (
        <PresentationFrame
          key={v.slug}
          videoSrc={`${R2}/${v.slug}/film.mp4`}
          poster={`${R2}/${v.slug}/poster.jpg`}
          slidesBase={`${R2}/${v.slug}/slides`}
          slideCount={v.slideCount}
          width={v.portrait ? 372 : 660}
          aspect={v.portrait ? 1080 / 1920 : 16 / 9}
          label={`${v.channel} · ${v.treatment} · ${v.voice} · ${v.duration}`}
          caption={`${v.title} — hook: ${v.hook} · ${v.pacing}`}
        />
      ))}
    </div>
  );
}

/** Kept so the slate can still stand alone as its own section if ever wanted. */
export function YouTubeSlateSection() {
  return (
    <SectionBox title="Format Slate" layout="custom">
      <SlateFilmGrid />
    </SectionBox>
  );
}
